import { useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import type { GlobalVariable } from '@mockd/shared';
import { api, ApiError } from '../utils/apiClient';

interface UseGlobalVariablesReturn {
  variables: GlobalVariable[];
  loading: boolean;
  error: string | null;
  upgradeRequired: boolean;
  fetchGlobalVariables: () => Promise<void>;
  createGlobalVariable: (key: string, value: unknown) => Promise<GlobalVariable>;
  updateGlobalVariable: (id: string, key: string, value: unknown) => Promise<GlobalVariable>;
  deleteGlobalVariable: (id: string) => Promise<void>;
}

export function useGlobalVariables(): UseGlobalVariablesReturn {
  const [variables, setVariables] = useState<GlobalVariable[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgradeRequired, setUpgradeRequired] = useState(false);

  const fetchGlobalVariables = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const json = await api.get<{ data: GlobalVariable[]; upgradeRequired?: boolean }>(
        '/api/global-variables'
      );
      setVariables(json.data || []);
      setUpgradeRequired(json.upgradeRequired ?? false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to fetch global variables');
    } finally {
      setLoading(false);
    }
  }, []);

  const createGlobalVariable = useCallback(async (
    key: string,
    value: unknown,
  ): Promise<GlobalVariable> => {
    try {
      const json = await api.post<{ data: GlobalVariable }>(
        '/api/global-variables',
        { key, value }
      );
      const newVar = json.data;
      setVariables(prev => [...prev, newVar]);
      toast.success('Global variable created');
      return newVar;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to create global variable';
      toast.error(message);
      throw err;
    }
  }, []);

  const updateGlobalVariable = useCallback(async (
    id: string,
    key: string,
    value: unknown,
  ): Promise<GlobalVariable> => {
    try {
      const json = await api.put<{ data: GlobalVariable }>(
        `/api/global-variables/${id}`,
        { key, value }
      );
      const updated = json.data;
      setVariables(prev => prev.map(v => v.id === id ? updated : v));
      toast.success('Global variable updated');
      return updated;
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to update global variable';
      toast.error(message);
      throw err;
    }
  }, []);

  const deleteGlobalVariable = useCallback(async (id: string): Promise<void> => {
    try {
      await api.del(`/api/global-variables/${id}`);
      setVariables(prev => prev.filter(v => v.id !== id));
      toast.success('Global variable deleted');
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to delete global variable';
      toast.error(message);
      throw err;
    }
  }, []);

  return {
    variables,
    loading,
    error,
    upgradeRequired,
    fetchGlobalVariables,
    createGlobalVariable,
    updateGlobalVariable,
    deleteGlobalVariable,
  };
}
